import { faAngleRight } from '@fortawesome/pro-regular-svg-icons'
import { ButtonAdd, Divider, Icon, Input, Toast } from '@seiue/ui'
import { useContext, useState } from 'react'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import styled from 'styled-components'

import { pluginApi, termApi } from 'packages/sdks-next/growp'
import { dragWeightReorder } from 'packages/utils'

import { getTheme } from '@/utils/theme'

import { TagContextParam } from '../../utils/type'
import { TagContext } from '../../utils/util'

/**
 * 分类管理 - 内容
 *
 * @returns 组件
 */
export const TagDrawerContent: React.FC = () => {
  const { tags, setCurrent, setShowTag, dragTags, setDragTags } =
    useContext<TagContextParam>(TagContext)

  const theme = getTheme()
  const [showInput, setShowInput] = useState(false)
  const fixedItems = tags.filter((e) => e.isVirtual === true)

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const newItems = dragWeightReorder(
      dragTags,
      result.source.index,
      result.destination.index
    )

    setDragTags(newItems)
  }

  const { api: createTag } = termApi.useCreateTerm({
    reload: [pluginApi.querySchoolPluginTags, pluginApi.querySchoolPlugins],
  })

  return (
    <Box>
      {/* 固定类型 */}
      {fixedItems.map((item) => (
        <FiexdItemBox key={`${item.id}-${item.name}-itembox`}>
          <FixedItem key={`${item.id}-${item.name}-item`}>
            <ItemSpace key={`${item.id}-space`} />
            <ItemName
              key={`${item.id}-name`}
              onClick={() => {
                setCurrent(1)
                setShowTag(item)
              }}
            >
              {item.name}
              <ItemCount key={`${item.id}-count`}>
                <>{item.count}个</>
                <Icon
                  size={16}
                  color={theme.border.greyDark}
                  icon={faAngleRight}
                  style={{ width: '16px' }}
                />
              </ItemCount>
            </ItemName>
          </FixedItem>
          <Divider key={`${item.id}-div`} />
        </FiexdItemBox>
      ))}
      {/* 可排序类型 */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {dragTags.map((item, index) => {
                return (
                  <Draggable
                    key={`${item.id}-${index}`}
                    draggableId={String(item.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <>
                        <Item
                          key={`${item.id}-${index}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}
                          $isDragging={snapshot.isDragging}
                          onClick={() => {
                            setCurrent(1)
                            setShowTag(item)
                          }}
                        >
                          <Icon
                            icon="Move"
                            size={14}
                            style={{
                              cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                            }}
                          />
                          <ItemName>
                            <div>{item.name}</div>
                            <ItemCount>
                              <>{item.count}个</>
                              <Icon
                                size={16}
                                color={theme.border.greyDark}
                                icon={faAngleRight}
                                style={{ width: '16px' }}
                              />
                            </ItemCount>
                          </ItemName>
                        </Item>
                        <Divider />
                      </>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {/* 新建类型输入框 */}
      {showInput ? (
        <>
          <FixedItem>
            <ItemSpace />

            <StyledInput
              onBlur={() => setShowInput(false)}
              onPressEnter={async (e) => {
                const { value } = e.target as HTMLInputElement

                const { data } = await createTag({
                  type: 'school_plugin_tag',
                  payload: {
                    name: value,
                    weight: 0,
                  },
                })

                setShowInput(false)
                setDragTags([
                  ...dragTags,
                  {
                    name: value,
                    id: data.id,
                    weight: 0,
                    count: 0,
                  },
                ])

                Toast.success('已成功新增分类')
              }}
            />
          </FixedItem>
          <Divider />
        </>
      ) : null}
      {/* 新建类型按钮 */}
      <>
        <FixedItem>
          <ButtonAdd
            style={{ paddingLeft: '4px' }}
            onClick={() => setShowInput(true)}
          >
            <StyledText>{'新增分类'}</StyledText>
          </ButtonAdd>
        </FixedItem>
        <Divider />
      </>
    </Box>
  )
}

const Box = styled.div`
  padding: 0 16px;
`

const FiexdItemBox = styled.div``

const Item = styled.div<{
  $isDragging?: boolean
}>`
  &:hover {
    background-color: ${(props) => props.theme.brand._5};
  }
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
  background-color: ${(props) =>
    props.$isDragging ? props.theme.brand._5 : '#fff'};

  padding: 20px 4px;
  gap: 8px;
  color: ${(props) => props.theme.text._3};
`

const FixedItem = styled.div`
  padding: 20px 4px;
  width: 100%;
  display: flex;
  gap: 8px;
  height: 56px;
  align-items: center;
`

const ItemName = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  color: ${(props) => props.theme.text._2};
`

const ItemCount = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  color: ${(props) => props.theme.text._3};
`

const ItemSpace = styled.div`
  width: 16px;
  height: 16px;
`

const StyledText = styled.span`
  margin-left: 8px !important;
  font-weight: 400;
`

const StyledInput = styled(Input)`
  width: 312px;
  border-color: ${(props) => props.theme.brand._1};
  border-radius: 8px;
`
